import React, { useRef, useEffect, ReactNode } from "react";

import cx from "classnames";

import { useUIDSeed } from "../../helpers/useUIDSeed";
import { Button } from "../Button";
import { Spacer } from "../Grid/Spacer";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

import styles from "./ControlWrapper.scss";

export interface ControlWrapperProps {
  /**
   * If true, the control will be styled for numeric input.
   */
  numeric?: boolean;

  /**
   * Text to display alongside numeric input.
   */
  numericText?: string;

  /**
   * If true, the control will be disabled.
   */
  disabled?: boolean;

  /**
   * Error message to display below the control.
   */
  error?: string | null;

  /**
   * Helper text to display below the control.
   */
  helperText?: ReactNode;

  /**
   * Label for the helper button.
   */
  helperButtonLabel?: React.ReactNode | null;

  /**
   * Callback function to handle helper button click.
   */
  helperButtonClick?: () => void;

  /**
   * Position of the helper button, either "top" or "bottom".
   */
  helperButtonPosition?: "top" | "bottom";

  /**
   * ID for the input element.
   */
  inputId: number | string;

  /**
   * Label for the control.
   */
  label?: ReactNode;

  /**
   * If true, the max length text will be displayed.
   */
  maxLengthText?: boolean;

  /**
   * Function to render the control.
   */
  renderControl: (props: any) => ReactNode;

  /**
   * If true, the control will be marked as required.
   */
  required?: boolean;

  /**
   * Callback function to handle clicks outside the control.
   */
  outsideClick?: null | (() => void);

  /**
   * Text to display for optional label.
   */
  optionalLabelText?: ReactNode;

  /**
   * Tooltip content to display.
   */
  tooltip?: ReactNode;

  /**
   * If true, the control will take the full width of its container.
   */
  fullWidth?: boolean;

  /**
   * Additional class names to apply to the wrapper.
   */
  wrapperClassNames?: string;

  /**
   * ID for the tooltip element.
   */
  tooltipId?: string;

  /**
   * Additional props to pass to the control.
   */
  [key: string]: any;
}

/**
 * A wrapper for individual form controls that allows you to decorate it with
 * consistent labels, helper text, optional text, and error messages.
 *
 * This is a low-level component and should be used in conjunction with another
 * form element, e.g., how `../CheckBox` works.
 */
// eslint-disable-next-line max-lines-per-function
export function ControlWrapper({
  numeric,
  numericText,
  disabled,
  error = "",
  helperText = "",
  helperButtonLabel = "",
  helperButtonClick,
  helperButtonPosition = "bottom",
  inputId,
  label,
  maxLengthText = true,
  renderControl,
  required,
  outsideClick,
  optionalLabelText,
  tooltip,
  fullWidth = false,
  wrapperClassNames = "",
  tooltipId,
  ...inputProps
}: ControlWrapperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const generateUID = useUIDSeed(inputId, label);

  const handleClick = (e: MouseEvent) => {
    if (
      ref.current &&
      e.target instanceof Node &&
      !ref.current.contains(e.target)
    ) {
      outsideClick?.();
    }
  };

  useEffect(() => {
    if (outsideClick) {
      document.addEventListener("click", handleClick, false);
      return () => {
        document.removeEventListener("click", handleClick, false);
      };
    }
  }, [outsideClick]);

  const labelClassNames = cx(styles["control-wrapper__label"], {
    [styles["control-wrapper__label--disabled"]]: disabled,
  });

  const labelId = generateUID("control-wrapper-label-id");
  const helperTextId = generateUID("control-wrapper-helper-text-id");
  const errorId = generateUID("control-wrapper-error-id");
  const { value, maxLength = 0, maxLengthAbove } = inputProps;

  const typed = safeToLocaleString(value ? value.length : 0);
  const total = safeToLocaleString(maxLength);
  const countString = `${typed}/${total}`;

  let helperRow;
  if (error || helperText || maxLength) {
    helperRow = (
      <div className={styles["control-wrapper__helper-container"]}>
        <div className={styles["control-wrapper__helper-text"]}>
          {maxLengthText && maxLength > 0 && !maxLengthAbove && (
            <div
              className={styles["control-wrapper__helper-count"]}
              data-testid="max-length-text"
            >
              {countString}
            </div>
          )}
          <div
            id={errorId}
            className={styles["control-wrapper__helper-text--error"]}
          >
            {error && (
              <>
                <Icon icon="warning" size={12} color={styles.systemRed} />
                <Spacer size="tiny" orientation="horizontal" />
              </>
            )}
            <div data-testid={`${errorId}-error-text`}>{error}</div>
          </div>
          <div id={helperTextId}>
            {helperText}
            {helperButtonLabel && helperButtonPosition === "bottom" && (
              <Button
                data-testid="helper-button"
                variant="link"
                onClick={helperButtonClick}
                className={styles["control-wrapper__helper-button"]}
              >
                {helperButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const inputWrapper = (
    <div className={styles["input-wrapper-container"]}>
      <div
        className={cx(styles["control-wrapper__input-wrapper"], {
          [styles.numeric]: numeric,
        })}
      >
        {renderControl({
          disabled,
          error,
          errorId,
          helperTextId,
          id: inputId,
          labelId,
          required,
          ...inputProps,
        })}
      </div>
      {numeric && (
        <div className={styles.numericText} data-testid="numeric-text">
          <Text size="paragraphSmall">{numericText}</Text>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cx(styles["control-wrapper"], wrapperClassNames, {
        [styles["control-wrapper-full-width"]]: fullWidth,
      })}
      ref={ref}
    >
      {label ? (
        <div className={styles["control-wrapper__label-wrapper"]}>
          <div className={styles["control-wrapper__label-container"]}>
            <div className={labelClassNames}>
              <label
                id={labelId}
                htmlFor={String(inputId)}
                className={styles["control-wrapper__label-wrapper"]}
                data-testid="control-wrapper-label"
              >
                {label}
              </label>
              {tooltip && (
                <>
                  <Spacer orientation="horizontal" size="small" />
                  <Tooltip
                    id={tooltipId}
                    onClick={(e) => {
                      // This way the input won't focus every time the tooltip is clicked
                      e.preventDefault();
                    }}
                    icon={true}
                    content={tooltip}
                  />
                </>
              )}
              {!required && optionalLabelText && (
                <span className={styles["control-wrapper__optional-label"]}>
                  {optionalLabelText}
                </span>
              )}
            </div>
            {maxLengthText && maxLength > 0 && maxLengthAbove ? (
              <div
                className={styles["control-wrapper__count-above"]}
                data-testid="max-length-text-above"
              >
                {countString}
              </div>
            ) : null}
            {helperButtonLabel && helperButtonPosition === "top" && (
              <Button
                data-testid="top-helper-button"
                variant="link"
                onClick={helperButtonClick}
                className={styles["control-wrapper__helper-button"]}
              >
                {helperButtonLabel}
              </Button>
            )}
          </div>
          {inputWrapper}
        </div>
      ) : (
        <>
          <label
            id={labelId}
            htmlFor={String(inputId)}
            className="screen-reader-only"
            data-testid="control-wrapper-label"
          >
            {inputProps.placeholder}
          </label>
          {inputWrapper}
        </>
      )}
      {helperRow}
    </div>
  );
}

function safeToLocaleString(
  numberOrString: number | string,
  options = {}
): string | number {
  return numberOrString && numberOrString.toLocaleString(undefined, options);
}
